import * as React from 'react';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import Button from 'antd/lib/button';
import locales from '../locales';
import * as paths from '../paths';
import {clientRentalHistoryPathTemplate, IdPageUrlParams, itemRentalHistoryPathTemplate} from '../paths';
import {RouteComponentProps, withRouter} from 'react-router';
import {ErrorResponse, GridRequest, GridRequestFilters, PageResponseEnvelope, RentalGridItem} from '../api/dtos/dtos';
import {ColumnProps} from 'antd/lib/table';
import {Table, Tag} from 'antd';
import {ErrorAlert} from '../common/ErrorAlert';
import furoApi from '../api/FuroApi';
import * as moment from 'moment';
import * as tableUtils from '../utils/table-utils';
import {filterableColumn, GridState, searchableSortableColumn, sortableColumn} from '../utils/table-utils';
import * as routerUtils from '../utils/router-utils';
import * as dateUtils from '../utils/date-utils';
import {getRentalItemStateFilters, renderStateElementCondensed, RentalState} from 'src/common/state';
import ButtonBar from 'src/common/ButtonBar';
import {Link} from 'react-router-dom';
import {renderItemNameWithMaintenanceIcon} from "../item/item-name-renderer";

import './RentalsPage.css';
import accountProvider from "../account/AccountProvider";
import ChecklistStateLabel from "../checklist/ChecklistStateLabel";
import IdStringLabel from "./IdStringLabel";
import {getRentalItemDepositStateFilters} from "../common/depositState";
import {groupNumber, withCurrency} from "../utils/number-utils";


interface RentalsPageState extends GridState<RentalGridItem> {
  error?: ErrorResponse;
  defaultFilters?: GridRequestFilters;
  hoveredRentalId?: number | null;
  hoveredReturnalId?: number | null;
  popupVisible: boolean;
}

const STATE_KEY: string = 'state';

const initialGridState = (): GridState<RentalGridItem> => {
  return {
    ...tableUtils.initialGridState(),
    filters: {
      [STATE_KEY]: [RentalState.RENTED, RentalState.RETURNED]
    }
  }
};

class RentalsPage extends React.Component<RouteComponentProps<IdPageUrlParams>, RentalsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<IdPageUrlParams>) {
    super(props);

    this.state = {
      // Default initial grid state
      ...initialGridState(),

      // Initial sorting
      sortColumnKey: 'rental.date',
      sortOrder: 'descend',

      // Initial filtering
      defaultFilters: this.getDefaultGridFilters(),

      // Override with cached state from Router
      ...this.props.location.state,

      popupVisible: false,
    };
  }

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const columns = this.createGridColumns();
    const buttonBarVisible = accountProvider.isAdmin() || accountProvider.isSales();
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        {buttonBarVisible &&
            <ButtonBar style={{marginBottom: 24}}>
                <Button type="primary" icon="download"
                        onClick={this.onReturnButtonClick}>{locales.return}</Button>
                <Button type="primary" icon="upload"
                        onClick={this.onRentButtonClick}>{locales.rental}</Button>
            </ButtonBar>
        }

        <Table
          className="rentals-table"
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          rowClassName={this.rowClassNameFn}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1444}}
        />
        <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>
      </>
    );
  }

  private createGridColumns(): Array<ColumnProps<RentalGridItem>> {
    const isClientRentals = this.isClientRentalHistoryPath();
    const isItemRentals = this.isItemRentalHistoryPath();
    return [
      {
        ...filterableColumn('', STATE_KEY, this.state, r => renderStateElementCondensed(r.state), getRentalItemStateFilters(), true),
        width: 38,
      }, {
        title: locales.rental_created,
        children: [
          sortableColumn(locales.date, 'rental.date', this.state, this.renterRentalDateColumn),
          searchableSortableColumn(locales.id, 'rental.idString', this.state, this.renderRentalCell, 200),
          {
            key: 'rental.checklist',
            width: 132,
            title: locales.checklist_state,
            render: this.renderRentalChecklistCell
          },
          filterableColumn(locales.caution, 'caution', this.state, this.renderRentalDepositState, getRentalItemDepositStateFilters(), false),
        ],
      }, {
        title: locales.return,
        children: [
          sortableColumn(locales.date, 'returnal.date', this.state, r => r.returnal && dateUtils.formatDateTimeOrNull(r.returnal.date)),
          searchableSortableColumn(locales.id, 'returnal.idString', this.state, this.renderReturnalCell, 194),
          {
            key: 'returnal.checklist',
            width: 132,
            title: locales.checklist_state,
            render: this.renderReturnalChecklistCell
          },
        ],
      },
      ...(isClientRentals ? [] : [
        searchableSortableColumn(locales.client, 'client.name', this.state, this.renderClientCell),
      ]),
      ...(isItemRentals ? [] : [
        searchableSortableColumn(locales.item, 'item.name', this.state, this.renderItemCell),
        searchableSortableColumn(locales.inventoryNumber, 'item.inventoryNumber', this.state, r => r.item.inventoryNumber),
      ]),
    ];
  }

  private getDefaultGridFilters(): GridRequestFilters {
    const filters: GridRequestFilters = {};

    if (this.isItemRentalHistoryPath()) {
      filters['item.id'] = [this.getIdFromPath()!];
    }
    if (this.isClientRentalHistoryPath()) {
      filters['client.id'] = [this.getIdFromPath()!];
    }

    return filters;
  }

  private getIdFromPath(): string | undefined {
    const {match} = this.props;
    return match.params.id;
  }

  private isItemRentalHistoryPath(): boolean {
    const {match} = this.props;
    return itemRentalHistoryPathTemplate === match.path;
  }

  private isClientRentalHistoryPath(): boolean {
    const {match} = this.props;
    return clientRentalHistoryPathTemplate === match.path;
  }

  private rowClassNameFn = (record: RentalGridItem, _: number) => {
    const {hoveredRentalId, hoveredReturnalId} = this.state;
    const highlighted = record.rental.id === hoveredRentalId || hoveredReturnalId === (record.returnal && record.returnal.id);
    return highlighted ? 'highlighted' : '';
  };

  private renterRentalDateColumn = (r: RentalGridItem): React.ReactNode => {
    const rentalDate = dateUtils.toMomentOrThrow(r.rental.date);
    const formattedRentalDate = rentalDate.format(dateUtils.MOMENT_DATE_TIME_FORMAT);
    const isFuture = rentalDate.isAfter(moment());
    return (
      <>
        {formattedRentalDate} {isFuture && <Tag color="blue">{locales.future_date}</Tag>}
      </>
    );
  };

  private renderClientCell = (record: RentalGridItem) => {
    return <Link to={paths.viewClientPath(record.client.id)}>{record.client.name}</Link>;
  };

  private renderItemCell = (record: RentalGridItem) => {
    return <Link
      to={paths.viewItemPath(record.item.id)}>{renderItemNameWithMaintenanceIcon(record.item.name, record.item.inMaintenance)}</Link>;
  };

  private renderRentalChecklistCell = (record: RentalGridItem) => {
    return <ChecklistStateLabel state={record.rentalChecklist.state}/>;
  }

  private renderRentalCell = (record: RentalGridItem) => {
    const onViewRentalClick = () => this.onViewRentalClick(record);
    const onRentalMouseOver = () => this.setState({hoveredRentalId: record.rental.id,});
    const onRentalMouseOut = () => this.setState({hoveredRentalId: undefined,});
    return (
      <IdStringLabel
        idString={record.rental.idString}
        type="rental"
        hasTransportLetter={record.hasRentalTransportLetter}
        onClick={onViewRentalClick}
        onMouseOver={onRentalMouseOver}
        onMouseOut={onRentalMouseOut}
      />
    );
  }

  private renderRentalDepositState = (record: RentalGridItem) => {
    return  record.caution > 0.0 && <>{withCurrency(groupNumber(record.caution))}</>;
  }

  private renderReturnalChecklistCell = (record: RentalGridItem) => {
    return record.returnal && <ChecklistStateLabel state={record.returnalChecklist.state}/>;
  }

  private renderReturnalCell = (record: RentalGridItem) => {
    const onViewReturnalClick = () => this.onViewReturnalClick(record);
    const onReturnalMouseOver = () => this.setState({hoveredReturnalId: record.returnal && record.returnal.id});
    const onReturnalMouseOut = () => this.setState({hoveredReturnalId: undefined,});
    return (record.returnal &&
        <IdStringLabel
            idString={record.returnal.idString}
            type="returnal"
            hasTransportLetter={record.hasReturnalTransportLetter}
            onClick={onViewReturnalClick}
            onMouseOver={onReturnalMouseOver}
            onMouseOut={onReturnalMouseOut}
        />
    );
  }

  private onGridChange = (newState: GridState<RentalGridItem>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onRentButtonClick = () => {
    this.props.history.push(paths.createRentalPath)
  };

  private onReturnButtonClick = () => {
    this.props.history.push(paths.createReturnalPath)
  };

  private reload = () => {
    const request = tableUtils.toGridRequest(this.state);
    this.addDefaultFiltersToGridRequest(request);

    const promise = furoApi.rentalItems.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private addDefaultFiltersToGridRequest(request: GridRequest) {
    request.filters = {
      ...request.filters,
      ...this.state.defaultFilters
    };
  }

  private onReloadSuccess = (response: PageResponseEnvelope<RentalGridItem>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };

  private onViewRentalClick = (record: RentalGridItem) => {
    this.props.history.push(paths.viewRentalPath(record.rental.id));
  };

  private onViewReturnalClick = (record: RentalGridItem) => {
    this.props.history.push(paths.viewReturnalPath(record.returnal && record.returnal.id));
  };

}

export default withRouter(RentalsPage);



// WEBPACK FOOTER //
// ./src/rental/RentalsPage.tsx